/**
 * Las formas de envío del proyecto: la lista y el formulario para añadir o
 * cambiar una.
 *
 * Una sola pieza para los dos sitios donde se tocan —el paso «Envío» del
 * asistente de un producto físico y la sección Ventas › Envíos—, porque son la
 * MISMA lista: las tarifas son del proyecto, no de cada producto, y valen para
 * todos. Por eso aquí cada cambio se guarda al momento en vez de esperar al
 * «Guardar» del asistente: cerrar el asistente sin crear el producto no puede
 * deshacer una tarifa que ya usan los demás.
 *
 * Tres formas, como Webflow: importe fijo, fijo pero gratis desde un total, o
 * recoger en el negocio. Lo de tarifas por peso o por zona vendrá después.
 */
import { CurrencySymbol } from './CurrencySymbol'
import { useState } from 'react'
import {
  IconBuildingStore,
  IconCheck,
  IconDots,
  IconLoader2,
  IconPencil,
  IconPlus,
  IconTrash,
  IconTruckDelivery,
} from '@tabler/icons-react'
import {
  createShippingRate,
  deleteShippingRate,
  updateShippingRate,
  type ShippingRate,
  type ShippingRateInput,
} from '../../../lib/api'
import { translateServerError, useI18n } from '../../../lib/i18n'
import { useRegion } from '../../../lib/region'
import { formatMoney, minorUnitFactor, toMinorUnits } from './offerShared'

type Draft = { name: string; kind: ShippingRate['kind']; amount: string; freeOver: string; pickupAddress: string }

const EMPTY: Draft = { name: '', kind: 'flat', amount: '', freeOver: '', pickupAddress: '' }

/** Un importe guardado, de vuelta a lo que se escribe en el campo. */
function toField(minor: number | null, currency: string): string {
  if (minor === null) return ''
  const factor = minorUnitFactor(currency)
  return (minor / factor).toFixed(factor === 1 ? 0 : 2)
}

/** La línea de debajo del nombre: cuánto cuesta o dónde se recoge. */
export function rateSummary(rate: Pick<ShippingRate, 'kind' | 'amount' | 'freeOver' | 'pickupAddress'>, currency: string, t: (key: string, vars?: Record<string, string | number>) => string): string {
  if (rate.kind === 'pickup') return rate.pickupAddress.trim() || t('shipping.rate.pickupNoAddress')
  const price = rate.amount > 0 ? formatMoney(rate.amount, currency) : t('shipping.rate.free')
  return rate.freeOver !== null
    ? t('shipping.rate.freeOver', { price, over: formatMoney(rate.freeOver, currency) })
    : price
}

export function ShippingRatesEditor({ projectId, token, rates, maxRates, onChange }: {
  projectId: string
  token: string
  rates: ShippingRate[]
  maxRates: number
  onChange: (rates: ShippingRate[]) => void
}) {
  const { t } = useI18n()
  const { currency } = useRegion()
  /** Qué se está editando: una tarifa por su id, `new` para una nueva, o nada. */
  const [editing, setEditing] = useState<string | null>(rates.length === 0 ? 'new' : null)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [busy, setBusy] = useState<string | null>(null)
  /* El menú «⋯» de una fila: Editar y Eliminar, como en el resto de listas. */
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [error, setError] = useState('')

  const open = (rate: ShippingRate | null) => {
    setError('')
    setEditing(rate?.id ?? 'new')
    setDraft(rate
      ? { name: rate.name, kind: rate.kind, amount: toField(rate.amount, currency), freeOver: toField(rate.freeOver, currency), pickupAddress: rate.pickupAddress }
      : { ...EMPTY, name: t('shipping.rate.defaultName') })
  }

  const save = async () => {
    const name = draft.name.trim()
    if (!name) { setError(t('shipping.rate.nameRequired')); return }
    const amount = draft.kind === 'pickup' ? 0 : toMinorUnits(draft.amount, currency) ?? 0
    const freeOver = draft.kind === 'pickup' || !draft.freeOver.trim() ? null : toMinorUnits(draft.freeOver, currency)
    const input: ShippingRateInput = { name, kind: draft.kind, amount, freeOver, pickupAddress: draft.kind === 'pickup' ? draft.pickupAddress.trim() : '', active: true }
    setBusy('save')
    setError('')
    try {
      if (editing === 'new') {
        const created = await createShippingRate(projectId, input, token)
        onChange([...rates, created])
      } else if (editing) {
        const { active: _active, ...patch } = input
        const updated = await updateShippingRate(projectId, editing, patch, token)
        onChange(rates.map((row) => (row.id === updated.id ? updated : row)))
      }
      setEditing(null)
    } catch (cause) {
      setError(translateServerError(cause, t('error.unexpected')))
    } finally {
      setBusy(null)
    }
  }

  const toggle = async (rate: ShippingRate) => {
    setBusy(rate.id)
    try {
      const updated = await updateShippingRate(projectId, rate.id, { active: !rate.active }, token)
      onChange(rates.map((row) => (row.id === updated.id ? updated : row)))
    } catch (cause) {
      setError(translateServerError(cause, t('error.unexpected')))
    } finally {
      setBusy(null)
    }
  }

  const remove = async (rate: ShippingRate) => {
    setBusy(rate.id)
    try {
      await deleteShippingRate(projectId, rate.id, token)
      onChange(rates.filter((row) => row.id !== rate.id))
      if (editing === rate.id) setEditing(null)
    } catch (cause) {
      setError(translateServerError(cause, t('error.unexpected')))
    } finally {
      setBusy(null)
    }
  }

  const form = (
    /* Dentro del asistente esto vive en su <form>: Enter aquí guarda la
       tarifa, no avanza de paso. */
    <div
      className="dash-ship-form"
      onKeyDown={(event) => {
        if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return
        event.preventDefault()
        void save()
      }}
    >
      <div className={`dash-svc-segmented${draft.kind === 'pickup' ? ' is-second' : ''}`} role="radiogroup" aria-label={t('shipping.rate.kind')}>
        <button type="button" role="radio" aria-checked={draft.kind === 'flat'} className={draft.kind === 'flat' ? 'is-on' : ''} onClick={() => setDraft((row) => ({ ...row, kind: 'flat', name: row.name === t('shipping.rate.pickupName') ? t('shipping.rate.defaultName') : row.name }))}>
          <IconTruckDelivery size={14} /> {t('shipping.rate.flat')}
        </button>
        <button type="button" role="radio" aria-checked={draft.kind === 'pickup'} className={draft.kind === 'pickup' ? 'is-on' : ''} onClick={() => setDraft((row) => ({ ...row, kind: 'pickup', name: row.name === t('shipping.rate.defaultName') ? t('shipping.rate.pickupName') : row.name }))}>
          <IconBuildingStore size={14} /> {t('shipping.rate.pickup')}
        </button>
      </div>
      <label className="dash-svc-field">
        <span>{t('shipping.rate.name')}</span>
        <input value={draft.name} maxLength={100} onChange={(event) => setDraft((row) => ({ ...row, name: event.target.value }))} />
        <small>{t('shipping.rate.nameHint')}</small>
      </label>
      {draft.kind === 'flat' ? (
        <div className="dash-ship-form-row">
          <label className="dash-svc-field">
            <span>{t('shipping.rate.amount')}</span>
            <div className="dash-ofw-amount">
              <CurrencySymbol currency={currency} />
              <input inputMode="decimal" value={draft.amount} placeholder="0.00" onChange={(event) => setDraft((row) => ({ ...row, amount: event.target.value }))} />
              <span className="dash-ofw-amount-currency">{currency.toUpperCase()}</span>
            </div>
            <small>{t('shipping.rate.amountHint')}</small>
          </label>
          <label className="dash-svc-field">
            <span className="dash-svc-label-row">{t('shipping.rate.freeOverLabel')}<em>{t('offers.new.optional')}</em></span>
            <div className="dash-ofw-amount">
              <CurrencySymbol currency={currency} />
              <input inputMode="decimal" value={draft.freeOver} onChange={(event) => setDraft((row) => ({ ...row, freeOver: event.target.value }))} />
              <span className="dash-ofw-amount-currency">{currency.toUpperCase()}</span>
            </div>
            <small>{t('shipping.rate.freeOverHint')}</small>
          </label>
        </div>
      ) : (
        <label className="dash-svc-field">
          <span>{t('shipping.rate.pickupAddress')}</span>
          <textarea rows={2} maxLength={500} value={draft.pickupAddress} placeholder={t('shipping.rate.pickupPlaceholder')} onChange={(event) => setDraft((row) => ({ ...row, pickupAddress: event.target.value }))} />
          <small>{t('shipping.rate.pickupHint')}</small>
        </label>
      )}
      {error ? <small className="dash-svc-field-error" role="alert">{error}</small> : null}
      <div className="dash-ship-form-actions">
        {rates.length > 0 ? <button type="button" className="dash-secondary-btn" disabled={busy === 'save'} onClick={() => { setEditing(null); setError('') }}>{t('offers.new.cancel')}</button> : null}
        <button type="button" className="dash-primary-btn" disabled={busy === 'save'} onClick={() => { void save() }}>
          {busy === 'save' ? <IconLoader2 size={14} className="dash-spin" /> : <IconCheck size={14} />}
          {t(editing === 'new' ? 'shipping.rate.add' : 'shipping.rate.save')}
        </button>
      </div>
    </div>
  )

  return (
    <div className="dash-ship-rates">
      {rates.length > 0 ? (
        <ul className="dash-offer-price-list">
          {rates.map((rate) => (
            editing === rate.id ? <li key={rate.id} className="dash-ship-editing">{form}</li> : (
              <li key={rate.id} className={rate.active ? '' : 'is-dropped dash-ship-off'}>
                <span className="dash-ship-icon" aria-hidden="true">{rate.kind === 'pickup' ? <IconBuildingStore size={16} /> : <IconTruckDelivery size={16} />}</span>
                <div className="dash-offer-price-copy">
                  <strong>{rate.name}</strong>
                  <span>{rateSummary(rate, currency, t)}</span>
                </div>
                <button
                  type="button"
                  className={`dash-page-settings-switch${rate.active ? ' is-on' : ''}`}
                  aria-pressed={rate.active}
                  aria-label={t('shipping.rate.activeAria', { name: rate.name })}
                  data-tooltip={t(rate.active ? 'shipping.rate.turnOff' : 'shipping.rate.turnOn')}
                  disabled={busy === rate.id}
                  onClick={() => { void toggle(rate) }}
                ><i /></button>
                {/* Las acciones en el «⋯» plano de las demás listas (Páginas,
                    Servicios, Copias…), no en dos botones sueltos. */}
                <div className={`dash-project-row-menu-wrap${menuOpenId === rate.id ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className={`project-menu-btn dash-page-options-btn${menuOpenId === rate.id ? ' active' : ''}`}
                    aria-label={t('common.optionsOf', { name: rate.name })}
                    aria-expanded={menuOpenId === rate.id}
                    disabled={busy === rate.id}
                    onClick={() => setMenuOpenId((id) => (id === rate.id ? null : rate.id))}
                  >
                    {busy === rate.id ? <IconLoader2 size={16} className="dash-spin" /> : <IconDots size={16} />}
                  </button>
                  {menuOpenId === rate.id ? (
                    <>
                      <div className="project-menu-backdrop" role="presentation" onClick={() => setMenuOpenId(null)} />
                      <div className="project-menu dash-project-row-menu" role="menu">
                        <button className="project-menu-item" type="button" role="menuitem" aria-label={t('shipping.rate.editAria', { name: rate.name })} onClick={() => { setMenuOpenId(null); open(rate) }}>
                          <IconPencil size={14} /> {t('shipping.rate.edit')}
                        </button>
                        <div className="project-menu-rule" />
                        <button className="project-menu-item danger" type="button" role="menuitem" aria-label={t('shipping.rate.removeAria', { name: rate.name })} onClick={() => { setMenuOpenId(null); void remove(rate) }}>
                          <IconTrash size={14} /> {t('shipping.rate.remove')}
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              </li>
            )
          ))}
        </ul>
      ) : null}
      {editing === 'new' ? form : rates.length < maxRates ? (
        <button type="button" className="dash-svc-image-drop is-compact" onClick={() => open(null)}>
          <IconPlus size={18} />
          <span><strong>{t('shipping.rate.addTitle')}</strong></span>
        </button>
      ) : <p className="dash-offer-card-foot">{t('shipping.rate.full', { max: maxRates })}</p>}
      {error && editing === null ? <small className="dash-svc-field-error" role="alert">{error}</small> : null}
    </div>
  )
}
